import { useState } from "react";
import { PrismLight as SyntaxHighlighter } from "react-syntax-highlighter";
import oneDark from "react-syntax-highlighter/dist/esm/styles/prism/one-dark";
import yaml from "react-syntax-highlighter/dist/esm/languages/prism/yaml";
import json from "react-syntax-highlighter/dist/esm/languages/prism/json";
import python from "react-syntax-highlighter/dist/esm/languages/prism/python";
import javascript from "react-syntax-highlighter/dist/esm/languages/prism/javascript";
import typescript from "react-syntax-highlighter/dist/esm/languages/prism/typescript";
import bash from "react-syntax-highlighter/dist/esm/languages/prism/bash";
import sql from "react-syntax-highlighter/dist/esm/languages/prism/sql";
import toml from "react-syntax-highlighter/dist/esm/languages/prism/toml";
import ini from "react-syntax-highlighter/dist/esm/languages/prism/ini";

// Fenced code in forum posts (```yaml … ```), highlighted with Prism. Only the
// languages people paste here are registered, to keep the bundle small.
SyntaxHighlighter.registerLanguage("yaml", yaml);
SyntaxHighlighter.registerLanguage("json", json);
SyntaxHighlighter.registerLanguage("python", python);
SyntaxHighlighter.registerLanguage("javascript", javascript);
SyntaxHighlighter.registerLanguage("typescript", typescript);
SyntaxHighlighter.registerLanguage("bash", bash);
SyntaxHighlighter.registerLanguage("sql", sql);
SyntaxHighlighter.registerLanguage("toml", toml);
SyntaxHighlighter.registerLanguage("ini", ini);

const ALIASES: Record<string, string> = {
  yml: "yaml",
  py: "python",
  js: "javascript",
  ts: "typescript",
  sh: "bash",
  shell: "bash",
  zsh: "bash",
};

export const CODE_LANGUAGES = ["yaml", "json", "python", "javascript", "typescript", "bash", "sql", "toml"];

const LABELS: Record<string, string> = {
  yaml: "YAML",
  json: "JSON",
  python: "Python",
  javascript: "JavaScript",
  typescript: "TypeScript",
  bash: "Bash",
  sql: "SQL",
  toml: "TOML",
  ini: "INI",
};

export const languageLabel = (lang: string) => LABELS[lang] ?? lang;

// Guess the language of unlabelled code from its first lines.
function guess(code: string): string | null {
  const s = code.trimStart();
  if (/^[[{]/.test(s)) {
    try {
      JSON.parse(code);
      return "json";
    } catch {
      /* not JSON */
    }
  }
  if (/^(sensor|automation|script|template|homeassistant|input_\w+|- (platform|alias|service|trigger)):?/m.test(s) || /^\w[\w-]*:\s*(\S.*)?$/m.test(s.split("\n")[0])) return "yaml";
  if (/^(def |import |from \w+ import|class \w+:)/m.test(s)) return "python";
  if (/^(\$ |curl |sudo |npm |pip |#!\/bin\/)/m.test(s)) return "bash";
  if (/^(SELECT|INSERT|UPDATE|WITH)\b/i.test(s)) return "sql";
  if (/^(const|let|function|import|export)\b/m.test(s)) return "javascript";
  return null;
}

export const CodeBlock: React.FC<{ code: string; lang?: string | null }> = ({ code, lang }) => {
  const [copied, setCopied] = useState(false);
  const raw = (lang ?? "").toLowerCase();
  const language = ALIASES[raw] ?? (raw || guess(code) || "text");
  const known = language in LABELS;

  return (
    <div className="fm-code">
      <div className="fm-code__bar">
        <span className="fm-code__lang">{known ? languageLabel(language) : raw || "Kod"}</span>
        <button
          type="button"
          className="fm-code__copy"
          onClick={() => {
            navigator.clipboard?.writeText(code).then(() => {
              setCopied(true);
              window.setTimeout(() => setCopied(false), 1500);
            });
          }}
        >
          {copied ? "✓ Kopierad" : "Kopiera"}
        </button>
      </div>
      <SyntaxHighlighter
        language={known ? language : "text"}
        style={oneDark}
        showLineNumbers={code.split("\n").length > 3}
        lineNumberStyle={{ minWidth: "2.2em", paddingRight: "1em", color: "#5c6370", userSelect: "none" }}
        customStyle={{ margin: 0, padding: "12px 14px", background: "transparent", fontSize: "0.84rem" }}
        codeTagProps={{ style: { fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace" } }}
      >
        {code}
      </SyntaxHighlighter>
    </div>
  );
};
